import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { alert } from 'actions/flash'
import { useUpdateMerchantMutation } from 'api/merchants'
import { isError } from 'api/utils'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import {
  markFormPersisted,
  setFormSubmitting,
} from 'slices/orderFeedbackReplyAddress'
import {
  selectAddressError,
  selectApiFormattedFormData,
  selectFormDisabled,
  selectIsFormDirty,
  selectIsFormSubmitting,
} from 'slices/orderFeedbackReplyAddress/selectors'
import { useSettingsEditPermission } from './useSettingsEditPermission'

const t = buildTranslate('order_ratings.settings.reply_address')

export const useReplyAddressSubmitForm = () => {
  const dispatch = useDispatch()
  const apiFormattedFormData = useSelector(selectApiFormattedFormData)
  const formDisabled = useSelector(selectFormDisabled)
  const isFormDirty = useSelector(selectIsFormDirty)
  const isFormSubmitting = useSelector(selectIsFormSubmitting)
  const addressError = useSelector(selectAddressError)
  const [updateMerchant] = useUpdateMerchantMutation()
  // FR-60. Presentation only: the server already carries
  // `rbac_authorize!('manage', 'merchant_settings')` on PATCH /merchants/:id.
  // This keeps a call the viewer cannot make off the wire.
  const { editDisabled } = useSettingsEditPermission()

  const handleSubmit = useCallback(async () => {
    // Guard the handler too, not just the button. `addressError` is here for
    // the same reason: a bypassed disabled prop must still not be able to
    // publish an address that fails validation under the fixed mode.
    if (
      editDisabled ||
      formDisabled ||
      isFormSubmitting ||
      !isFormDirty ||
      addressError
    )
      return

    dispatch(setFormSubmitting(true))
    try {
      const result = await updateMerchant({
        data: apiFormattedFormData,
      })

      if (isError(result)) {
        dispatch(
          alert({
            key: 'danger',
            message: t('alert.submit_error'),
            timeout: 5,
          })
        )
        return
      }

      // A 200 is not proof the write landed. Reach an environment that does
      // not yet carry thanx/nexus#1976 and Rails strong params DROP both keys
      // from the PATCH, then answer 200 with the untouched merchant — the
      // pane would go clean, claim success, and quietly revert to the
      // server's values on the next load. The read path already defends with
      // `??` fallbacks; this is the write path's equivalent. The response
      // body is the saved merchant under a `merchant` root (Grape's
      // `root 'merchants', 'merchant'` on MerchantEntity), so compare what
      // came back against what we sent: a dropped key comes back `undefined`,
      // which matches neither a mode nor a null address.
      const echoed = result.data?.merchant
      // 'user' mode sends a null address, and a merchant that has never had a
      // fixed address can echo it as `''` — the same "no fixed address" state,
      // so treat the two as equal here. `undefined` is deliberately NOT part
      // of that equivalence: it is exactly the dropped-key signal.
      const echoedAddress = echoed?.order_feedback_reply_address
      const persisted =
        echoed?.order_feedback_reply_mode ===
          apiFormattedFormData.order_feedback_reply_mode &&
        echoedAddress !== undefined &&
        (echoedAddress || null) ===
          (apiFormattedFormData.order_feedback_reply_address || null)

      if (!persisted) {
        dispatch(
          alert({
            key: 'danger',
            message: t('alert.submit_error'),
            timeout: 5,
          })
        )
        return
      }

      // The edit is now the persisted value, so the pane goes clean and
      // Publish disables again until the next change.
      dispatch(markFormPersisted())
      dispatch(
        alert({
          key: 'success',
          message: t('alert.submit_success'),
          timeout: 5,
        })
      )
    } finally {
      // Always clear isFormSubmitting — covers the normal isError path AND a
      // thrown error from any middleware in the dispatch chain, not just the
      // normal error path.
      dispatch(setFormSubmitting(false))
    }
  }, [
    apiFormattedFormData,
    updateMerchant,
    dispatch,
    editDisabled,
    formDisabled,
    isFormDirty,
    isFormSubmitting,
    addressError,
  ])

  return handleSubmit
}
